/**
 * Thermal Physics Module for HELIAC System
 * Implements realistic thermal dynamics and constraints
 */

export class ThermalPhysics {
  constructor() {
    // HELIAC System specifications
    this.specs = {
      htes: {
        minTemp: 300,        // °C - Minimum operating temperature
        maxTemp: 350,        // °C - Maximum operating temperature
        nominalTemp: 350,    // °C - Nominal operating temperature
        heatCapacity: 0.5,   // MWh/°C per MWh storage capacity
        thermalLossRate: 0.002, // 0.2% loss per hour at nominal temp
        chargeMinTemp: 340,  // °C - Minimum temp for charging (must be hot enough)
        dischargeMinTemp: 310, // °C - Minimum temp for turbine operation
      },
      ltes: {
        minTemp: 85,         // °C - Minimum operating temperature
        maxTemp: 95,         // °C - Maximum operating temperature
        nominalTemp: 95,     // °C - Nominal operating temperature
        heatCapacity: 1.0,   // MWh/°C per MWh storage capacity
        thermalLossRate: 0.001, // 0.1% loss per hour
      },
      turbine: {
        minInletTemp: 310,   // °C - Minimum HTES temp for turbine operation (overrides allowed via config)
        optimalInletTemp: 340, // °C - Optimal inlet temperature
        efficiency: {
          heat: 0.85,        // 85% to heat at optimal temp
          electricity: 0.10, // 10% to electricity at optimal temp
        },
        tempEfficiencyFactor: 0.003, // Efficiency reduction per °C below optimal
      },
      heater: {
        maxPower: 45,        // MW - Maximum electric heater power
        targetTemp: {
          htes: 350,         // °C - Target temp when charging HTES
          ltes: 95,          // °C - Target temp when charging LTES
        }
      }
    };

    // Current state
    this.state = {
      htes: {
        temperature: 350,    // °C
        energy: 0,           // MWh stored
        lastUpdate: 0,       // Hour of last update
      },
      ltes: {
        temperature: 95,     // °C
        energy: 0,           // MWh stored
        lastUpdate: 0,       // Hour of last update
      }
    };

    // Violation tracking
    this.violations = [];
    this.warningThreshold = 5; // °C from limits
  }

  /**
   * Initialize thermal state from storage levels
   */
  initializeFromStorage(htesLevel, ltesLevel, htesCapacity, ltesCapacity) {
    // Calculate temperature based on energy level
    // Higher energy = higher temperature (simplified linear model)
    const htesEnergy = htesLevel * htesCapacity;
    const ltesEnergy = ltesLevel * ltesCapacity;

    // HTES: Map 0-100% to minTemp-maxTemp range
    this.state.htes.temperature = this.specs.htes.minTemp +
      (this.specs.htes.maxTemp - this.specs.htes.minTemp) * htesLevel;
    this.state.htes.energy = htesEnergy;

    // LTES: Map 0-100% to minTemp-maxTemp range
    this.state.ltes.temperature = this.specs.ltes.minTemp +
      (this.specs.ltes.maxTemp - this.specs.ltes.minTemp) * ltesLevel;
    this.state.ltes.energy = ltesEnergy;
  }

  /**
   * Apply thermal losses over time
   */
  applyThermalLosses(currentHour) {
    const hoursPassed = currentHour - this.state.htes.lastUpdate;
    if (hoursPassed <= 0) return;

    // HTES losses (higher temp = higher losses)
    const htesTempRatio = this.state.htes.temperature / this.specs.htes.nominalTemp;
    const htesLoss = this.state.htes.energy * this.specs.htes.thermalLossRate * hoursPassed * htesTempRatio;
    this.state.htes.energy = Math.max(0, this.state.htes.energy - htesLoss);

    // Update temperature based on energy loss
    if (this.state.htes.energy > 0) {
      const tempLoss = (htesLoss / this.state.htes.energy) *
        (this.state.htes.temperature - this.specs.htes.minTemp) * 0.5;
      this.state.htes.temperature = Math.max(
        this.specs.htes.minTemp,
        this.state.htes.temperature - tempLoss
      );
    }

    // LTES losses (simpler, more stable)
    const ltesLoss = this.state.ltes.energy * this.specs.ltes.thermalLossRate * hoursPassed;
    this.state.ltes.energy = Math.max(0, this.state.ltes.energy - ltesLoss);

    // Update temperature based on energy loss
    if (this.state.ltes.energy > 0) {
      const tempLoss = (ltesLoss / this.state.ltes.energy) *
        (this.state.ltes.temperature - this.specs.ltes.minTemp) * 0.3;
      this.state.ltes.temperature = Math.max(
        this.specs.ltes.minTemp,
        this.state.ltes.temperature - tempLoss
      );
    }

    this.state.htes.lastUpdate = currentHour;
    this.state.ltes.lastUpdate = currentHour;
  }

  /**
   * Check if HTES can be charged (temperature constraints)
   */
  canChargeHTES() {
    // Can always add energy to HTES if below max temp
    return this.state.htes.temperature < this.specs.htes.maxTemp;
  }

  /**
   * Check if LTES can be charged
   */
  canChargeLTES() {
    // Can always add energy to LTES if below max temp
    return this.state.ltes.temperature < this.specs.ltes.maxTemp;
  }

  /**
   * Check if HTES can discharge through turbine
   */
  canDischargeHTES() {
    return this.state.htes.temperature >= this.specs.turbine.minInletTemp;
  }

  /**
   * Calculate turbine efficiency based on HTES temperature
   */
  getTurbineEfficiency() {
    if (this.state.htes.temperature < this.specs.turbine.minInletTemp) {
      return { heat: 0, electricity: 0 };
    }

    const tempDiff = Math.max(0,
      this.specs.turbine.optimalInletTemp - this.state.htes.temperature
    );
    const efficiencyFactor = Math.max(0.7, 1 - tempDiff * this.specs.turbine.tempEfficiencyFactor);

    return {
      heat: this.specs.turbine.efficiency.heat * efficiencyFactor,
      electricity: this.specs.turbine.efficiency.electricity * efficiencyFactor,
    };
  }

  /**
   * Update state after HTES charge
   */
  updateHTESCharge(energyAdded, currentHour) {
    this.applyThermalLosses(currentHour);

    const oldEnergy = this.state.htes.energy;
    this.state.htes.energy += energyAdded;

    // Temperature increases with energy addition
    // Simplified: Adding energy at heater target temp
    const avgTemp = (this.state.htes.temperature * oldEnergy +
                    this.specs.heater.targetTemp.htes * energyAdded) /
                    (oldEnergy + energyAdded || 1);

    this.state.htes.temperature = Math.min(this.specs.htes.maxTemp, avgTemp);
    this.state.htes.lastUpdate = currentHour;
  }

  /**
   * Update state after HTES discharge
   */
  updateHTESDischarge(energyRemoved, currentHour) {
    this.applyThermalLosses(currentHour);

    this.state.htes.energy = Math.max(0, this.state.htes.energy - energyRemoved);

    // Temperature decreases with energy removal
    // More energy removed = more temperature drop
    if (this.state.htes.energy > 0) {
      const energyRatio = this.state.htes.energy / (this.state.htes.energy + energyRemoved);
      const tempDrop = (this.state.htes.temperature - this.specs.htes.minTemp) * (1 - energyRatio) * 0.5;
      this.state.htes.temperature = Math.max(
        this.specs.htes.minTemp,
        this.state.htes.temperature - tempDrop
      );
    } else {
      this.state.htes.temperature = this.specs.htes.minTemp;
    }

    this.state.htes.lastUpdate = currentHour;
  }

  /**
   * Update state after LTES charge
   */
  updateLTESCharge(energyAdded, currentHour) {
    this.applyThermalLosses(currentHour);

    const oldEnergy = this.state.ltes.energy;
    this.state.ltes.energy += energyAdded;

    // Temperature increases with energy addition
    const avgTemp = (this.state.ltes.temperature * oldEnergy +
                    this.specs.heater.targetTemp.ltes * energyAdded) /
                    (oldEnergy + energyAdded || 1);

    this.state.ltes.temperature = Math.min(this.specs.ltes.maxTemp, avgTemp);
    this.state.ltes.lastUpdate = currentHour;
  }

  /**
   * Update state after LTES discharge
   */
  updateLTESDischarge(energyRemoved, currentHour) {
    this.applyThermalLosses(currentHour);

    this.state.ltes.energy = Math.max(0, this.state.ltes.energy - energyRemoved);

    // Temperature decreases slightly with discharge
    if (this.state.ltes.energy > 0) {
      const energyRatio = this.state.ltes.energy / (this.state.ltes.energy + energyRemoved);
      const tempDrop = (this.state.ltes.temperature - this.specs.ltes.minTemp) * (1 - energyRatio) * 0.2;
      this.state.ltes.temperature = Math.max(
        this.specs.ltes.minTemp,
        this.state.ltes.temperature - tempDrop
      );
    }

    this.state.ltes.lastUpdate = currentHour;
  }

  /**
   * Check for constraint violations
   */
  checkViolations(actions, hour) {
    const violations = [];

    // Check for simultaneous HTES/LTES charging via HEATER (exclude waste heat)
    const htesHeaterCharge = actions.filter(a =>
      a.type && (
        a.type === 'OP_CHARGE_HTES_SOLAR' ||
        a.type === 'OP_CHARGE_HTES_GRID' ||
        a.type === 'CHARGE_HTES_NEGATIVE' ||
        a.type === 'HTES_CHARGE'
      )
    );
    const ltesHeaterCharge = actions.filter(a =>
      a.type && (
        a.type === 'OP_CHARGE_LTES_SOLAR' ||
        a.type === 'OP_CHARGE_LTES_GRID' ||
        a.type === 'CHARGE_LTES_NEGATIVE' ||
        a.type === 'LTES_CHARGE'
      )
    );

    // Allow simultaneous charging ONLY ved negative priser (multi-cycle strategi)
    const hasNegativePriceActions = actions.some(a =>
      a.type && (
        a.type === 'CHARGE_HTES_NEGATIVE' ||
        a.type === 'CHARGE_LTES_NEGATIVE' ||
        a.type === 'TURBINE_NEGATIVE_PRICE_ARBITRAGE'
      )
    );

    if (htesHeaterCharge.length > 0 && ltesHeaterCharge.length > 0 && !hasNegativePriceActions) {
      violations.push({
        hour,
        type: 'SIMULTANEOUS_CHARGING',
        severity: 'CRITICAL',
        message: `Simultaneous HTES and LTES charging via heater detected - physically impossible with single heater`,
        htesActions: htesHeaterCharge.map(a => a.type),
        ltesActions: ltesHeaterCharge.map(a => a.type),
      });
    }

    // Check temperature constraints
    if (this.state.htes.temperature > this.specs.htes.maxTemp) {
      violations.push({
        hour,
        type: 'HTES_OVERTEMP',
        severity: 'CRITICAL',
        message: `HTES temperature ${this.state.htes.temperature.toFixed(1)}°C exceeds max ${this.specs.htes.maxTemp}°C`,
        temperature: this.state.htes.temperature,
      });
    }

    if (this.state.htes.temperature < this.specs.htes.minTemp - 10) {
      violations.push({
        hour,
        type: 'HTES_UNDERTEMP',
        severity: 'WARNING',
        message: `HTES temperature ${this.state.htes.temperature.toFixed(1)}°C below minimum ${this.specs.htes.minTemp}°C`,
        temperature: this.state.htes.temperature,
      });
    }

    // Check turbine operation at low temperature
    const turbineActions = actions.filter(a =>
      a.type && (a.type.includes('TURBINE') || a.type.includes('HTES_TO_DH'))
    );

    if (turbineActions.length > 0 && this.state.htes.temperature < this.specs.turbine.minInletTemp) {
      violations.push({
        hour,
        type: 'TURBINE_LOW_TEMP',
        severity: 'CRITICAL',
        message: `Turbine operation at ${this.state.htes.temperature.toFixed(1)}°C below minimum ${this.specs.turbine.minInletTemp}°C`,
        temperature: this.state.htes.temperature,
        actions: turbineActions.map(a => a.type),
      });
    }

    // Store violations
    if (violations.length > 0) {
      this.violations.push(...violations);
    }

    return violations;
  }

  /**
   * Get current thermal state
   */
  getState() {
    return {
      htes: {
        temperature: this.state.htes.temperature,
        energy: this.state.htes.energy,
        canCharge: this.canChargeHTES(),
        canDischarge: this.canDischargeHTES(),
        efficiency: this.getTurbineEfficiency(),
      },
      ltes: {
        temperature: this.state.ltes.temperature,
        energy: this.state.ltes.energy,
        canCharge: this.canChargeLTES(),
      },
      violations: this.violations.length,
    };
  }

  /**
   * Get all violations
   */
  getViolations() {
    return this.violations;
  }

  /**
   * Clear violations (for new simulation run)
   */
  clearViolations() {
    this.violations = [];
  }
}

export default ThermalPhysics;